import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../request/http-common';
import { setUser } from './userSlice';
import { setAuthStatus } from '../../request/Auth/authSlice';
import {
  Box, Typography, FormControl, OutlinedInput, InputLabel,
  InputAdornment, Alert, Link
} from '@mui/material';
import { Email, ArrowForward, LockReset, Shield, TrendingUp, Speed } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const AuthInput = styled(OutlinedInput)(({ theme }) => ({
  borderRadius: '10px',
  fontSize: '15px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    borderWidth: '1.5px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600', borderWidth: '2px' },
}));

const SubmitButton = styled('button')(({ disabled }) => ({
  width: '100%',
  height: '52px',
  border: 'none',
  borderRadius: '10px',
  background: disabled ? 'rgba(247,166,0,0.4)' : 'linear-gradient(135deg, #F7A600 0%, #FFB52E 100%)',
  color: '#0a0d1a',
  fontWeight: 700,
  fontSize: '16px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.2s',
  boxShadow: disabled ? 'none' : '0 4px 20px rgba(247,166,0,0.35)',
}));

const steps = [
  { num: '01', title: 'Enter your email', desc: 'Provide the email linked to your account.' },
  { num: '02', title: 'Check your inbox', desc: 'We\'ll send a verification code instantly.' },
  { num: '03', title: 'Set new password', desc: 'Use the code to reset your password securely.' },
];

const Forgot = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appMode = useSelector((state) => state.mode.mode);

  const [error, setErros] = useState('');
  const [registerForm, setRegisterForm] = useState({ email: '' });

  const onChange = (e) => setRegisterForm({ ...registerForm, [e.target.name]: e.target.value });

  const { isLoading: isSendingRequest, mutate: postLogin } = useMutation(
    async () => await apiClient.post('/api/forgot-password', { email: registerForm.email }),
    {
      onSuccess: (res) => {
        localStorage.setItem('notification', JSON.stringify({ message: `${res.data.message}`, type: 'success' }));
        window.dispatchEvent(new Event('processed'));
        window.dispatchEvent(new Event('newMessage'));
        navigate('/update-password');
      },
      onError: (err) => {
        setErros(err.response?.data?.message || err.message);
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  const onSubmit = () => {
    window.dispatchEvent(new Event('processing'));
    postLogin();
  };

  const isDark = appMode === 'dark';

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: isDark ? '#080c18' : '#f1f5f9' }}>

      {/* ── Left Brand Panel ── */}
      <Box sx={{
        width: { xs: 0, lg: '45%' },
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: { lg: 5, xl: 7 },
        background: 'linear-gradient(160deg, #080c18 0%, #0d1526 55%, #111e35 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: '-80px', right: '-80px', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(247,166,0,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '-60px', left: '-60px', width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(247,166,0,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '32px 32px', pointerEvents: 'none' }} />

        <Box>
          <img src="/intracapital-logos/intracapital-logo-white.png" alt="INTRA CAPITAL" style={{ height: '44px', marginBottom: '48px' }} />

          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ height: '2px', width: '28px', bgcolor: '#F7A600' }} />
            <Typography sx={{ color: '#F7A600', fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              Account Recovery
            </Typography>
          </Box>

          <Typography sx={{ fontSize: { lg: '34px', xl: '42px' }, fontWeight: 800, color: '#fff', lineHeight: 1.2, mb: 3 }}>
            Secure Account{' '}
            <Box component="span" sx={{ background: 'linear-gradient(90deg, #F7A600, #FFD166)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Recovery.
            </Box>
          </Typography>

          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.7, mb: 6, maxWidth: 360 }}>
            Regaining access to your account is quick and secure. Follow the three simple steps below.
          </Typography>

          {/* Steps */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {steps.map((step, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
                  background: 'linear-gradient(135deg, rgba(247,166,0,0.2), rgba(247,166,0,0.05))',
                  border: '1px solid rgba(247,166,0,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Typography sx={{ color: '#F7A600', fontWeight: 800, fontSize: '13px' }}>{step.num}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '14px', mb: 0.3 }}>{step.title}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>{step.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px' }}>
          © {new Date().getFullYear()} INTRA CAPITAL LIMITED. All rights reserved.
        </Typography>
      </Box>

      {/* ── Right Form Panel ── */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 3, sm: 5 },
        bgcolor: isDark ? '#0d1117' : '#ffffff',
        overflowY: 'auto',
      }}>
        <Box sx={{ width: '100%', maxWidth: '420px' }}>

          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'block', lg: 'none' }, textAlign: 'center', mb: 5 }}>
            <img src={isDark ? '/intracapital-logos/intracapital-logo-white.png' : '/intracapital-logos/intracapital-logo.png'} alt="INTRA CAPITAL" style={{ height: '44px' }} />
          </Box>

          {/* Icon */}
          <Box sx={{
            width: 64, height: 64, borderRadius: '16px', mb: 4,
            background: 'linear-gradient(135deg, rgba(247,166,0,0.15), rgba(247,166,0,0.05))',
            border: '1px solid rgba(247,166,0,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <LockReset sx={{ fontSize: 30, color: '#F7A600' }} />
          </Box>

          {/* Heading */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontSize: '28px', fontWeight: 800, color: isDark ? '#fff' : '#0f172a', mb: 1 }}>
              Reset your password
            </Typography>
            <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)', fontSize: '15px', lineHeight: 1.6 }}>
              Enter your registered email and we'll send you a verification code to reset your password.
            </Typography>
          </Box>

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: '10px', fontSize: '14px' }}>{error}</Alert>
          )}

          {/* Field */}
          <FormControl fullWidth sx={{ mb: 4 }}>
            <InputLabel sx={{ '&.Mui-focused': { color: '#F7A600' } }}>Email Address</InputLabel>
            <AuthInput
              error={!!error}
              onChange={onChange}
              id="email"
              type="email"
              name="email"
              label="Email Address"
              startAdornment={
                <InputAdornment position="start">
                  <Email sx={{ color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', fontSize: '19px' }} />
                </InputAdornment>
              }
            />
          </FormControl>

          {/* Submit */}
          <SubmitButton onClick={onSubmit} disabled={isSendingRequest}>
            {isSendingRequest ? 'Sending Code…' : 'Send Verification Code'}
            {!isSendingRequest && <ArrowForward sx={{ fontSize: 18 }} />}
          </SubmitButton>

          {/* Links */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Link component={RouterLink} to="/login" underline="none" sx={{ color: '#F7A600', fontSize: '14px', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
              ← Back to Sign In
            </Link>
            <Link component={RouterLink} to="/register" underline="none" sx={{ color: '#F7A600', fontSize: '14px', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
              Create Account
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Forgot;
